/**
 * Recover an already-open tab from a stale deploy.
 *
 * Every release changes the content-hashed chunk names under /assets/. A tab
 * that stayed open across the release still holds the old index.html, so its
 * next lazy navigation (React.lazy route, lazily loaded panel) requests a
 * chunk the server no longer has and Vite dispatches `vite:preloadError` on
 * `window`. Reloading fetches the new index.html and its matching chunks.
 *
 * Guard: at most one automatic reload per RELOAD_COOLDOWN_MS, tracked in
 * sessionStorage, so a genuinely broken build surfaces its error instead of
 * reload-looping. If sessionStorage is unusable we do not reload at all,
 * because the loop could not be detected.
 *
 * Side-effect module: importing it installs the listener (same pattern as
 * `@/utils/monacoTheme`). `installPreloadErrorReload` is exported for
 * tests.
 */
const STORAGE_KEY = 'brewlytics:preload-error-reload-at';
const RELOAD_COOLDOWN_MS = 30_000;

export function installPreloadErrorReload(win: Window = window): void {
  win.addEventListener('vite:preloadError', (event) => {
    const now = Date.now();
    try {
      const last = Number(win.sessionStorage.getItem(STORAGE_KEY) ?? '0') || 0;
      if (now - last < RELOAD_COOLDOWN_MS) {
        // We reloaded moments ago and it still fails: let Vite rethrow so the
        // failure is visible instead of looping.
        return;
      }
      win.sessionStorage.setItem(STORAGE_KEY, String(now));
    } catch {
      return; // cannot track the cooldown, so never risk a reload loop
    }
    event.preventDefault(); // handled here; suppress Vite's rethrow
    win.location.reload();
  });
}

if (typeof window !== 'undefined') {
  installPreloadErrorReload();
}
